import { recordRecentChat } from "./recent-opened";
import {
  cancelPendingConversationSelection,
  trackPendingConversationSelection,
} from "@/lib/pending-conversation-frame";
import {
  selectConversation,
  type ConversationSelectionReceipt,
  type DurableConversation,
} from "@/api/conversations";
import type { Frame } from "@/api/spaces";
import { hydrateConversationFrameHistory } from "@/lib/conversation-frame-history-cache";
import { durableConversationInputTarget } from "@/lib/durable-conversation-input-target";
import {
  cancelPendingConversationFrameReveals,
  conversationFrameRevealToken,
  isConversationFrameRevealCurrent,
  revealConversationFrameResult,
} from "@/lib/conversation-frame-reveal";
import { prewarmAgentSession } from "@/lib/agent-prewarm";
import { selectProjectByIdentity } from "@/lib/select-project-space";
import { useWorkspaceLoadStore } from "@/stores/workspace-load-store";
import { useFramesStore } from "@/stores/frames-store";
import { selectFramesByType } from "@/stores/frames-selectors";
import type { SystemToast } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useWorkspaceConversationOverlay } from "@/stores/workspace-conversation-overlay-store";
import { taskChatKind } from "@/lib/task-chat-kind";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";
import {
  PROJECT_ARCHIVED_REASON,
  type ConversationReadOnlyReason,
} from "@/lib/conversation-read-only-reason";

interface OpenDurableConversationFrameArgs {
  conversation: DurableConversation;
  addToast: (toast: SystemToast) => void;
  closePopover: () => void;
  selectAgent: (id: string | null) => void;
  shouldContinue?: () => boolean;
}

function durableConversationFrame(
  conversation: DurableConversation,
  selection: ConversationSelectionReceipt | null,
  options: {
    readOnly: boolean;
    /** A `ConversationReadOnlyReason` code, or a server message. */
    readOnlyReason: string | null;
    canStartNewConversation: boolean;
    spaceId: string;
  },
): Frame {
  const runtimeSessionId = selection?.runtime_session_id ?? undefined;
  const activeExecutionId = selection?.active_execution_id ?? undefined;
  const persistedSegment = conversation.frame_runtime_segment;
  const persistedSegmentMatches = Boolean(
    persistedSegment &&
    (!runtimeSessionId ||
      persistedSegment.runtime_session_id === runtimeSessionId),
  );
  const sessionModel = persistedSegmentMatches
    ? (persistedSegment?.model ?? undefined)
    : undefined;
  const contextPercent =
    persistedSegmentMatches && persistedSegment?.context_percent !== null
      ? persistedSegment?.context_percent
      : undefined;
  const contextUsedTokens = persistedSegmentMatches
    ? persistedSegment?.context_used_tokens
    : undefined;
  const contextWindowMax = persistedSegmentMatches
    ? (persistedSegment?.context_window_max ?? undefined)
    : undefined;
  const framesState = useFramesStore.getState();
  const existing = selectFramesByType(framesState, "conversation").find(
    (frame) =>
      (frame.metadata.conversationId ?? frame.metadata.conversation_id) ===
      conversation.id,
  );
  const { overlay } = useWorkspaceConversationOverlay.getState();
  const overlayFrame =
    overlay?.project.id === conversation.project_id &&
    (overlay.frame.metadata.conversationId ??
      overlay.frame.metadata.conversation_id) === conversation.id
      ? overlay.frame
      : null;
  const existingId = overlayFrame?.id ?? existing?.frameId;
  const occupied = framesState.framesById[conversation.id];
  const reusedForAnotherChat =
    occupied && occupied.metadata.conversationId !== conversation.id;
  return {
    id:
      existingId ??
      (reusedForAnotherChat ? crypto.randomUUID() : conversation.id),
    space: options.spaceId,
    frame_type: "conversation",
    title:
      conversation.display_title?.trim() ||
      conversation.title.trim() ||
      (conversation.agent_name
        ? translate(libChatMessages, "chatWithAgent", {
            agent: conversation.agent_name,
          })
        : translate(libChatMessages, "chatWithAnAgent")),
    content: {},
    status: "idle",
    position_x: overlayFrame?.position_x ?? 0,
    position_y: overlayFrame?.position_y ?? 0,
    width: overlayFrame?.width ?? 400,
    height: overlayFrame?.height ?? 300,
    agent_id: null,
    parent_frame: null,
    metadata: {
      conversationFrame: true,
      transientConversationFrame: true,
      conversationId: conversation.id,
      taskChat: taskChatKind(conversation.context_key),
      ...(conversation.task_id ? { taskId: conversation.task_id } : {}),
      conversationReadOnly: options.readOnly,
      ...(options.readOnly && options.readOnlyReason
        ? { conversationReadOnlyReason: options.readOnlyReason }
        : {}),
      ...(options.readOnlyReason === PROJECT_ARCHIVED_REASON
        ? { conversationReadOnlyProject: conversation.project_name ?? "" }
        : {}),
      conversationCanStartNewConversation: options.canStartNewConversation,
      sourceProjectId: conversation.project_id,
      ...(conversation.space_id
        ? { sourceSpaceId: conversation.space_id }
        : {}),
      ...(runtimeSessionId ? { liveSessionId: runtimeSessionId } : {}),
      ...(activeExecutionId ? { liveExecutionId: activeExecutionId } : {}),
      ...(sessionModel ? { sessionModel } : {}),
      ...(persistedSegmentMatches
        ? {
            sessionAdapterType: persistedSegment?.adapter_type,
            sessionThinkingEffort: persistedSegment?.thinking_effort,
            sessionRuntimeSource: persistedSegment?.runtime_source,
          }
        : {}),
      ...(contextPercent !== undefined ? { contextPercent } : {}),
      ...(contextUsedTokens !== undefined ? { contextUsedTokens } : {}),
      ...(contextWindowMax !== undefined ? { contextWindowMax } : {}),
      ...(conversation.project_agent_id
        ? { projectAgentId: conversation.project_agent_id }
        : {}),
      ...(selection?.agent_key ? { agentKey: selection.agent_key } : {}),
      ...(conversation.agent_name
        ? { agentName: conversation.agent_name }
        : {}),
    },
    edited_by_user_at: null,
    created_at: conversation.created_at,
    updated_at: conversation.activity_at || conversation.updated_at,
    task_ticket_number: null,
    task_id: conversation.task_id,
    task_title: conversation.task?.title ?? null,
  };
}

function isConversationSelectionConflict(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    error.status === 409
  );
}

function selectionConflictReason(error: unknown): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof error.data === "object" &&
    error.data !== null &&
    "error" in error.data &&
    typeof error.data.error === "string" &&
    error.data.error.trim()
  ) {
    return error.data.error.trim();
  }
  return "chat_agent_unavailable" satisfies ConversationReadOnlyReason;
}

export async function openDurableConversationFrame({
  conversation,
  addToast,
  closePopover,
  selectAgent,
  shouldContinue,
}: OpenDurableConversationFrameArgs): Promise<void> {
  cancelPendingConversationSelection();
  cancelPendingConversationFrameReveals();
  const revealToken = conversationFrameRevealToken();
  const shouldReveal = () =>
    isConversationFrameRevealCurrent(revealToken) &&
    (shouldContinue?.() ?? true);

  closePopover();

  try {
    // A task chat's input goes to its worker; it is never selected for Denker.
    const taskChat = taskChatKind(conversation.context_key);
    const canResume = conversation.lifecycle?.actions.resume?.allowed ?? true;
    let selection: ConversationSelectionReceipt | null = null;
    let readOnly = !canResume;
    let readOnlyReason: string | null = readOnly
      ? conversation.lifecycle?.actions.resume?.reason?.trim() ||
        ("chat_unavailable" satisfies ConversationReadOnlyReason)
      : null;
    let canStartNewConversation =
      !taskChat && (canResume || conversation.lifecycle?.lifecycle !== "open");
    const previous = useProjectStore.getState();
    const targetChanged =
      previous.currentProject?.id !== conversation.project_id;
    const shouldUseCurrentSpace = conversation.project_status === "archived";
    const selected = shouldUseCurrentSpace
      ? false
      : await selectProjectByIdentity(
          {
            projectId: conversation.project_id,
            projectName: conversation.project_name,
          },
          { addSystemToast: addToast, shouldContinue: shouldReveal },
        );
    if (!shouldReveal()) return;
    if (selected && targetChanged) {
      useWorkspaceLoadStore.setState({
        framesLoadedSpaceId: null,
        loadedSpaceId: null,
      });
    }
    const state = useProjectStore.getState();
    const project = state.currentProject;
    if (!project) throw new Error("No canvas available for chat");
    const space = state.currentSpace ?? {
      id: project.id,
      project: project.id,
      name: project.name,
      ticket_prefix: project.ticket_prefix ?? "T",
      canvas_state: project.canvas_state ?? {},
      color: project.color,
      is_pinned: project.is_pinned,
      is_onboarding: false,
      sort_order: project.sort_order,
      created_at: project.created_at,
      updated_at: project.updated_at,
    };
    let pending = canResume && selected && !taskChat;
    const pendingFrame = durableConversationFrame(conversation, null, {
      readOnly: true,
      readOnlyReason: "opening_chat" satisfies ConversationReadOnlyReason,
      canStartNewConversation: false,
      spaceId: project.id,
    });
    const settlePending = pending
      ? trackPendingConversationSelection(pendingFrame)
      : () => {};
    if (pending) {
      revealConversationFrameResult(
        {
          frame: pendingFrame,
          project,
          space,
          created: false,
          transient: true,
        },
        () => pending && shouldReveal(),
      );
    }
    if (canResume && selected && !taskChat) {
      try {
        selection = await selectConversation(conversation.id);
      } catch (error) {
        readOnly = true;
        readOnlyReason = isConversationSelectionConflict(error)
          ? selectionConflictReason(error)
          : ("select_again_to_retry" satisfies ConversationReadOnlyReason);
        canStartNewConversation = false;
        if (!isConversationSelectionConflict(error) && shouldReveal()) {
          addToast({
            id: `conversation-open-err-${conversation.id}-${Date.now()}`,
            level: "error",
            title: translate(libChatMessages, "couldNotConnectTitle"),
            description: translate(
              libChatMessages,
              "couldNotConnectDescription",
            ),
          });
        }
      } finally {
        pending = false;
        settlePending();
      }
    }
    if (
      !shouldReveal() ||
      useProjectStore.getState().currentProject?.id !== project.id ||
      (useProjectStore.getState().currentSpace?.id ??
        useProjectStore.getState().currentProject?.id) !== space.id
    )
      return;
    const selectedConversation = selection
      ? {
          ...conversation,
          project_agent_id: selection.project_agent_id,
          agent_name: conversation.agent_name ?? selection.agent_key,
        }
      : conversation;
    if (!selected) {
      readOnly = true;
      canStartNewConversation = false;
      readOnlyReason =
        conversation.project_status === "archived"
          ? PROJECT_ARCHIVED_REASON
          : ("chat_project_unavailable" satisfies ConversationReadOnlyReason);
    }
    if (selected && !taskChat)
      selectAgent(selectedConversation.project_agent_id);
    const activeSelection = selected ? selection : null;
    const frame = durableConversationFrame(
      selectedConversation,
      activeSelection,
      {
        readOnly,
        readOnlyReason,
        canStartNewConversation,
        spaceId: project.id,
      },
    );
    if (activeSelection) {
      useInputBarTarget
        .getState()
        .setTarget(
          durableConversationInputTarget(selectedConversation, activeSelection),
        );
      prewarmAgentSession("conversation_open");
    }
    const result = {
      frame,
      project,
      space,
      created: false,
      transient: true,
    };
    revealConversationFrameResult(result, shouldReveal);
    if (shouldReveal()) recordRecentChat(conversation);
    void hydrateConversationFrameHistory({
      projectId: project.id,
      spaceId: project.id,
      frameId: frame.id,
      conversationId: conversation.id,
      // A background worker may have finished while its live events were missed.
      ...(taskChat ? { refresh: true } : {}),
      shouldContinue: shouldReveal,
    }).catch(() => {});
  } catch {
    if (!shouldReveal()) return;
    addToast({
      id: `conversation-open-err-${conversation.id}-${Date.now()}`,
      level: "error",
      title: translate(libChatMessages, "couldNotOpenChat"),
    });
  }
}
